import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useWaitingVariantTracking } from '../useWaitingVariantTracking';
import { shuffleItems } from '../waitingVariants';

export type MemoramaPair = {
	pairId: string;
	emoji: string;
	label: string;
};

export type MemoramaDeck = {
	id: string;
	title: string;
	emoji: string;
	pairs: MemoramaPair[];
};

type MemoramaCard = MemoramaPair & {
	cardId: number;
};

const FLIP_BACK_DELAY_MS = 800;

const buildCards = (deck: MemoramaDeck): MemoramaCard[] =>
	shuffleItems(deck.pairs.flatMap((pair) => [pair, pair])).map(
		(pair, index) => ({
			...pair,
			cardId: index,
		}),
	);

// Motor de memorama: juega un deck de pares y rota de deck en cada "Jugar de
// nuevo". El contenido vive en content/memoramaDecks.ts.
const MemoramaEngine = ({ decks }: { decks: MemoramaDeck[] }) => {
	const { trackInteraction } = useWaitingVariantTracking();
	const [deckIndex, setDeckIndex] = useState(() =>
		Math.floor(Math.random() * decks.length),
	);
	const [cards, setCards] = useState<MemoramaCard[]>(() =>
		buildCards(decks[deckIndex]),
	);
	const [flippedCardIds, setFlippedCardIds] = useState<number[]>([]);
	const [matchedPairIds, setMatchedPairIds] = useState<string[]>([]);
	const [moves, setMoves] = useState(0);
	const flipBackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (flipBackTimeoutRef.current) clearTimeout(flipBackTimeoutRef.current);
		};
	}, []);

	const deck = decks[deckIndex];
	const isGameWon = matchedPairIds.length === deck.pairs.length;

	const handleCardTap = (card: MemoramaCard) => {
		if (
			flippedCardIds.length === 2 ||
			flippedCardIds.includes(card.cardId) ||
			matchedPairIds.includes(card.pairId)
		) {
			return;
		}

		const nextFlipped = [...flippedCardIds, card.cardId];
		setFlippedCardIds(nextFlipped);

		if (nextFlipped.length < 2) return;

		setMoves((prev) => prev + 1);
		const [firstCard] = cards.filter((c) => c.cardId === nextFlipped[0]);
		if (firstCard.pairId === card.pairId) {
			if (matchedPairIds.length + 1 === deck.pairs.length) {
				trackInteraction('memorama_won', { deck: deck.id, moves: moves + 1 });
			}
			setMatchedPairIds((prev) => [...prev, card.pairId]);
			setFlippedCardIds([]);
			return;
		}

		flipBackTimeoutRef.current = setTimeout(() => {
			setFlippedCardIds([]);
		}, FLIP_BACK_DELAY_MS);
	};

	const handlePlayAgain = () => {
		const nextDeckIndex = (deckIndex + 1) % decks.length;
		trackInteraction('memorama_replayed', { deck: decks[nextDeckIndex].id });
		setDeckIndex(nextDeckIndex);
		setCards(buildCards(decks[nextDeckIndex]));
		setFlippedCardIds([]);
		setMatchedPairIds([]);
		setMoves(0);
	};

	if (isGameWon) {
		return (
			<div className='flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl bg-h-primary-95 px-5 py-6 text-center'>
				<span className='text-4xl'>🎉</span>
				<h3 className='text-base font-semibold text-h-text-heading'>
					¡Completaste el memorama!
				</h3>
				<p className='text-sm text-h-neutral-50'>
					Encontraste los {deck.pairs.length} pares en {moves} movimientos.
				</p>
				<Button
					onClick={handlePlayAgain}
					className='bg-h-primary-50 mt-1 h-11 rounded-xl px-6 font-semibold text-white'
				>
					Jugar de nuevo
				</Button>
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-3 rounded-xl bg-h-primary-95 px-4 py-5'>
			<div className='flex items-center justify-between'>
				<p className='text-[11px] font-bold uppercase tracking-[0.08em] text-h-primary-40'>
					{deck.emoji} {deck.title}
				</p>
				<p className='text-xs text-h-neutral-50'>
					{matchedPairIds.length}/{deck.pairs.length} pares · {moves} mov.
				</p>
			</div>
			<p className='text-sm text-h-neutral-50'>Encuentra los pares.</p>
			<div className='grid grid-cols-4 gap-2'>
				{cards.map((card) => {
					const isRevealed =
						flippedCardIds.includes(card.cardId) ||
						matchedPairIds.includes(card.pairId);
					const isMatched = matchedPairIds.includes(card.pairId);
					return (
						<button
							key={card.cardId}
							type='button'
							onClick={() => handleCardTap(card)}
							aria-label={isRevealed ? card.label : 'Carta oculta'}
							className={`flex aspect-square flex-col items-center justify-center gap-0.5 rounded-lg border transition-colors duration-200 ${
								isMatched
									? 'border-h-success-50 bg-h-success-95'
									: isRevealed
										? 'border-h-primary-70 bg-white'
										: 'border-transparent bg-h-primary-90'
							}`}
						>
							{isRevealed ? (
								<>
									<span className='text-xl md:text-2xl'>{card.emoji}</span>
									<span className='px-0.5 text-center text-[9px] font-medium leading-tight text-h-neutral-50'>
										{card.label}
									</span>
								</>
							) : (
								<span className='text-lg font-bold text-h-primary-40'>?</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

export default MemoramaEngine;
